/**
 * Session — the object the UI actually talks to.
 *
 * It composes the four layers below it (auth → device link → RPC mux →
 * allowlist) into the small surface the app needs, and owns the state that has
 * to survive a reconnect:
 *
 * - **One link per device.** Switching devices closes the old link and opens a
 *   new one; the chat watches are rebound through `rebind`.
 * - **Allowlist enforcement.** Every outbound call goes through `call` /
 *   `subscribe`, so a method the browser is not allowed to name cannot reach
 *   the wire even by accident.
 * - **Watch registry.** `WatchChats`, `WatchSessions`, `WatchDocMessages` and
 *   friends are registered once and reissued verbatim on every reconnect, since
 *   the RPC protocol has no resume token.
 */

import { AuthClient, decodeJwtOrgId, decodeJwtUserId } from "./auth";
import { DeviceLink, type LinkState, type LinkTimers, type SocketFactory } from "./device-link";
import { RpcClient, RpcError } from "./rpc-client";
import { checkMethod, isStreamMethod, RpcDeniedError } from "./allowlist";
import type {
  Chat,
  ChatConfig,
  Device,
  EngineInfo,
  HarnessDescriptor,
  Model,
  Session,
  Space
} from "./models";

export interface SessionOptions {
  edgeUrl: string;
  auth: AuthClient;
  socketFactory?: SocketFactory;
  /** Injectable link clocks (tests only). */
  timers?: Partial<LinkTimers>;
  onState?: (state: SessionState) => void;
}

export interface SessionState {
  auth: "anonymous" | "authenticated";
  link: LinkState;
  /** Detail from the last link transition (e.g. `host_offline`). */
  detail?: string;
  deviceId?: string;
  /** Set when the link failed for a reason the user must act on. */
  problem?: string;
}

/** A registered watch: reissued on every reconnect. */
interface WatchRegistration {
  method: string;
  params: unknown;
  onItem: (item: unknown) => void;
  onError?: (error: RpcError) => void;
  subscription?: { cancel: () => void };
}

export class SessionNotReadyError extends Error {
  constructor(message = "not connected to a device") {
    super(message);
    this.name = "SessionNotReadyError";
  }
}

export class ZeronSession {
  readonly rpc: RpcClient;
  private link: DeviceLink | undefined;
  private state: SessionState;
  private deviceId: string | undefined;
  private readonly watches = new Map<string, WatchRegistration>();
  private readonly options: SessionOptions;
  /** Additional state listeners (the store subscribes here; `options.onState` is
   * the single owner-supplied hook). */
  private readonly stateListeners = new Set<(state: SessionState) => void>();

  constructor(options: SessionOptions) {
    this.options = options;
    this.state = { auth: "anonymous", link: "idle" };
    // The transport reports `open` from the link so the mux can drop frames
    // early instead of queueing them behind a dead socket; `send` reports a
    // mid-flight failure so the UI can say why nothing happened.
    const session = this;
    this.rpc = new RpcClient({
      send: (frame) => {
        if (session.link?.sendRpc(frame) !== true) {
          session.setProblem("the link is down — reconnecting");
        }
      },
      get open(): boolean {
        return session.link?.isOpen === true;
      }
    });
  }

  get currentState(): SessionState {
    return this.state;
  }

  /**
   * Subscribe to link/auth transitions. The listener fires immediately with the
   * current state so a late subscriber (the store, which is built after the
   * session) is never left believing the link is `idle`.
   */
  subscribeState(listener: (state: SessionState) => void): () => void {
    this.stateListeners.add(listener);
    listener(this.state);
    return () => void this.stateListeners.delete(listener);
  }

  /** Single funnel for state changes: keeps `stateListeners` in sync with the
   * `options.onState` hook, which predates it. */
  private publish(): void {
    this.options.onState?.(this.state);
    for (const listener of this.stateListeners) listener(this.state);
  }

  get currentDeviceId(): string | undefined {
    return this.deviceId;
  }

  /** The WorkOS org the session is scoped to, if any (needed for registry reads). */
  get orgId(): string | undefined {
    const token = this.options.auth.accessToken;
    return token === undefined ? undefined : decodeJwtOrgId(token);
  }

  get userId(): string | undefined {
    const token = this.options.auth.accessToken;
    return token === undefined ? undefined : decodeJwtUserId(token);
  }

  /**
   * (Re)connect to a device. Idempotent for the same device unless `force`.
   *
   * Resolves only once the relay has accepted the socket, so callers can issue
   * RPC immediately afterwards.
   *
   * `force` tears the existing link down first. That matters for the Retry
   * button: an existing link that is sitting in `reconnecting` is still an
   * object, so the idempotence check would return instantly and the retry would
   * do nothing at all.
   */
  async connect(deviceId: string, options: { force?: boolean } = {}): Promise<void> {
    if (this.deviceId === deviceId && this.link !== undefined && options.force !== true) return;
    // A forced redial must not leave the old socket's events alive: it is about
    // to be replaced, and a late `onclose` from it would schedule a second dial.
    this.link?.close("reconnecting");
    this.link = undefined;
    this.deviceId = deviceId;
    this.link = new DeviceLink({
      edgeUrl: this.options.edgeUrl,
      deviceId,
      tokenProvider: async () => {
        const token = await this.options.auth.ensureAccessToken();
        if (token === undefined) {
          this.setProblem("your session expired — sign in again");
        }
        return token;
      },
      socketFactory: this.options.socketFactory,
      timers: this.options.timers,
      onState: (link, detail) => {
        this.state = { ...this.state, link, detail };
        this.publish();
      },
      onRpcText: (text) => this.rpc.handleText(text),
      onWarning: () => {},
      onReady: () => {
        // Every reconnect is a fresh connId with no server-side state: the
        // watches must be reissued from scratch, and only once the socket is
        // genuinely open.
        this.state = { ...this.state, auth: "authenticated", problem: undefined };
        this.publish();
        this.reissueWatches();
      }
    });
    this.state = { ...this.state, auth: "authenticated", deviceId };
    this.publish();
    await this.link.start();
    // `start()` resolves when the socket is created, not when the relay accepts
    // it; frames sent before then are dropped.
    await this.link.waitForOnline();
  }

  /** Drop the link and forget all watches (sign-out, device removal). */
  disconnect(): void {
    this.link?.close("disconnected");
    this.link = undefined;
    this.deviceId = undefined;
    this.clearWatches();
    this.rpc.close("disconnected");
    this.state = { auth: "anonymous", link: "closed" };
    this.publish();
  }

  /** Ask the link to redial or re-echo now (visibility change, Retry button). */
  probe(): void {
    this.link?.probeNow();
  }

  /**
   * Tear the link down and dial again. Used by the Retry button and after a
   * visibility change that finds the socket dead — `probe()` only re-echoes, it
   * cannot recover a socket that has already closed.
   */
  async reconnect(): Promise<void> {
    const deviceId = this.deviceId;
    if (deviceId === undefined) return;
    await this.connect(deviceId, { force: true });
  }

  /**
   * Unary call, allowlist-checked. Throws [`RpcDeniedError`] before anything is
   * sent when the method is not exposed to the browser.
   */
  async call<T = unknown>(
    method: string,
    params: unknown = {},
    options: { timeoutMs?: number; targetDeviceId?: string } = {}
  ): Promise<T> {
    const fullParams = this.withTarget(params, options.targetDeviceId);
    this.assertAllowed(method, fullParams);
    if (this.link === undefined) throw new SessionNotReadyError();
    return this.rpc.call<T>(method, fullParams, { timeoutMs: options.timeoutMs });
  }

  /**
   * Register a streaming watch. The registration is remembered and reissued on
   * every reconnect, so callers register once and forget.
   *
   * `key` identifies the watch for replacement (e.g. `doc:chat-1`); registering
   * the same key twice cancels the first subscription.
   */
  watch(
    key: string,
    method: string,
    params: unknown,
    handlers: {
      onItem: (item: unknown) => void;
      onError?: (error: RpcError) => void;
      targetDeviceId?: string;
    }
  ): void {
    const fullParams = this.withTarget(params, handlers.targetDeviceId);
    this.assertAllowed(method, fullParams);
    const previous = this.watches.get(key);
    previous?.subscription?.cancel();
    const registration: WatchRegistration = {
      method,
      params: fullParams,
      onItem: handlers.onItem,
      onError: handlers.onError
    };
    this.watches.set(key, registration);
    if (this.link !== undefined) this.startWatch(registration);
  }

  /** Stop a watch by key. Idempotent. */
  unwatch(key: string): void {
    const registration = this.watches.get(key);
    if (registration === undefined) return;
    registration.subscription?.cancel();
    this.watches.delete(key);
  }

  /** Stop every watch (device switch, sign-out). */
  clearWatches(): void {
    for (const registration of this.watches.values()) registration.subscription?.cancel();
    this.watches.clear();
  }

  // -- convenience wrappers for the daily loop ------------------------------

  engineInfo(targetDeviceId?: string): Promise<EngineInfo> {
    return this.call<EngineInfo>("EngineInfo", {}, { targetDeviceId });
  }

  listHarnesses(targetDeviceId?: string): Promise<HarnessDescriptor[]> {
    return this.call<HarnessDescriptor[]>("ListHarnesses", {}, { targetDeviceId });
  }

  listModels(harness: string, targetDeviceId?: string): Promise<Model[]> {
    return this.call<Model[]>("ListModels", { harness }, { targetDeviceId });
  }

  /**
   * Send a prompt as a durable Run command. `messageId` is client-minted and is
   * the dedup key — the same id must never be reused for different content, and
   * reusing it for the same content is exactly how a retry stays idempotent.
   */
  runPrompt(options: {
    chatId: string;
    prompt: string;
    messageId: string;
    cwd: string;
    harness?: string;
    model?: string | null;
    reasoning?: string | null;
    sandbox?: string;
    autoApprove?: boolean;
    resume?: string | null;
    targetDeviceId?: string;
  }): Promise<{ commandId: string }> {
    const request: Record<string, unknown> = {
      prompt: options.prompt,
      model: options.model ?? null,
      reasoning: options.reasoning ?? null,
      modelOptions: {},
      cwd: options.cwd,
      sandbox: options.sandbox ?? "workspaceWrite",
      autoApprove: options.autoApprove ?? false,
      resume: options.resume ?? null,
      attachments: []
    };
    if (options.harness !== undefined) request.harness = options.harness;
    return this.call<{ commandId: string }>(
      "QueueCommand",
      {
        chatId: options.chatId,
        command: { kind: "run", messageId: options.messageId, request },
        transfers: []
      },
      { targetDeviceId: options.targetDeviceId }
    );
  }

  /** Steer into the live turn without interrupting it. */
  steer(options: {
    chatId: string;
    prompt: string;
    messageId?: string;
    targetDeviceId?: string;
  }): Promise<{ commandId: string }> {
    const command: Record<string, unknown> = { kind: "steer", prompt: options.prompt };
    if (options.messageId !== undefined) command.messageId = options.messageId;
    return this.call<{ commandId: string }>(
      "QueueCommand",
      { chatId: options.chatId, command },
      { targetDeviceId: options.targetDeviceId }
    );
  }

  interrupt(chatId: string, targetDeviceId?: string): Promise<{ commandId: string }> {
    return this.call<{ commandId: string }>(
      "QueueCommand",
      { chatId, command: { kind: "interrupt" } },
      { targetDeviceId }
    );
  }

  /** Answer the agent's pending question. */
  respondInput(options: {
    chatId: string;
    requestId: string;
    answers: { questionId: string; labels: string[] }[];
    targetDeviceId?: string;
  }): Promise<{ commandId: string }> {
    return this.call<{ commandId: string }>(
      "QueueCommand",
      {
        chatId: options.chatId,
        command: {
          kind: "respondInput",
          requestId: options.requestId,
          answers: options.answers
        }
      },
      { targetDeviceId: options.targetDeviceId }
    );
  }

  /** Hold a message in the chat's durable queue (not the prompt path). */
  queueMessage(options: {
    chatId: string;
    text: string;
    holdForTurnEnd?: boolean;
    sendAt?: number | null;
    targetDeviceId?: string;
  }): Promise<{ id: string }> {
    return this.call<{ id: string }>(
      "QueueMessage",
      {
        chatId: options.chatId,
        text: options.text,
        attachments: [],
        holdForTurnEnd: options.holdForTurnEnd ?? true,
        sendAt: options.sendAt ?? null,
        config: null
      },
      { targetDeviceId: options.targetDeviceId }
    );
  }

  removeQueued(chatId: string, id: string, targetDeviceId?: string): Promise<{ removed: boolean }> {
    return this.call("RemoveQueuedMessage", { chatId, id }, { targetDeviceId });
  }

  sendQueuedNow(chatId: string, id: string, targetDeviceId?: string): Promise<{ sent: boolean }> {
    return this.call("SendQueuedMessageNow", { chatId, id }, { targetDeviceId });
  }

  steerQueuedNow(chatId: string, id: string, targetDeviceId?: string): Promise<{ sent: boolean }> {
    return this.call("SteerQueuedMessageNow", { chatId, id }, { targetDeviceId });
  }

  renameChat(chatId: string, title: string): Promise<{ ok: boolean }> {
    return this.call("Mutate", { op: "renameChat", chatId, title });
  }

  setChatArchived(chatId: string, archived: boolean): Promise<{ ok: boolean }> {
    return this.call("Mutate", { op: "setChatArchived", chatId, archived });
  }

  markChatSeen(chatId: string): Promise<{ ok: boolean }> {
    return this.call("Mutate", { op: "markChatSeen", chatId });
  }

  /**
   * Mint an empty chat on a device.
   *
   * The engine needs a host: `spaceId` (a project, which fixes device + folder)
   * or `deviceId` (a project-less chat, cwd `~`). Idempotent by id, so a retry
   * after a dropped reply cannot create a second chat.
   */
  createChat(options: {
    chatId: string;
    spaceId?: string;
    deviceId?: string;
    /** The harness/model/reasoning/sandbox the first run will use. */
    config?: ChatConfig;
    /** The picked base ref, named on the row from the first frame. */
    branch?: string;
    /** An isolated worktree's path, overriding the project folder. */
    cwd?: string;
    targetDeviceId?: string;
  }): Promise<{ ok: boolean }> {
    const params: Record<string, unknown> = { op: "createChat", chatId: options.chatId };
    if (options.spaceId !== undefined) params.spaceId = options.spaceId;
    if (options.deviceId !== undefined) params.deviceId = options.deviceId;
    if (options.config !== undefined) params.config = options.config;
    if (options.branch !== undefined) params.branch = options.branch;
    if (options.cwd !== undefined) params.cwd = options.cwd;
    return this.call("Mutate", params, { targetDeviceId: options.targetDeviceId });
  }

  /** Branches and worktrees of a repository, for the new-chat screen's pickers.
   * Read-only: nothing here touches the repository. */
  listRefs(repoPath: string, targetDeviceId?: string): Promise<unknown> {
    return this.call("ListRefs", { repoPath }, { targetDeviceId });
  }

  listWorktrees(repoPath: string, targetDeviceId?: string): Promise<unknown> {
    return this.call("ListWorktrees", { repoPath }, { targetDeviceId });
  }

  /**
   * The repository's change requests, for browsing.
   *
   * Addressed by CHECKOUT (`cwd`), not by repository coordinates: the caller is a
   * conversation and what it knows is where its checkout is, while the host is
   * what can read that checkout's remote. Passing coordinates from here would be
   * asking the browser to derive something it cannot see.
   */
  listChangeRequests(
    cwd: string,
    state: "open" | "closed" | "all" = "open",
    targetDeviceId?: string
  ): Promise<unknown> {
    return this.call("ListChangeRequests", { cwd, state }, { targetDeviceId });
  }

  /** One change request's detail: description, checks, mergeability. */
  getChangeRequestDetail(
    cwd: string,
    locator: { host: string; owner: string; repository: string; number: number },
    targetDeviceId?: string
  ): Promise<unknown> {
    return this.call("GetChangeRequestDetail", { cwd, locator }, { targetDeviceId });
  }

  // -- internals ------------------------------------------------------------

  private reissueWatches(): void {
    for (const registration of this.watches.values()) this.startWatch(registration);
  }

  private startWatch(registration: WatchRegistration): void {
    registration.subscription?.cancel();
    try {
      registration.subscription = this.rpc.subscribe(
        registration.method,
        registration.params,
        {
          onItem: registration.onItem,
          onError: (error) => {
            registration.onError?.(error);
          },
          onDone: () => {
            // A watch that ends on its own (engine restart, chat closed) is
            // reissued on the next reconnect; nothing to do here.
          }
        },
        { timeoutMs: isStreamMethod(registration.method) ? 20_000 : 30_000 }
      );
    } catch (error) {
      registration.onError?.(error as RpcError);
    }
  }

  private withTarget(params: unknown, targetDeviceId?: string): unknown {
    if (targetDeviceId === undefined) return params;
    const base = (typeof params === "object" && params !== null ? params : {}) as Record<
      string,
      unknown
    >;
    return { ...base, targetDeviceId };
  }

  private assertAllowed(method: string, params: unknown): void {
    const decision = checkMethod(method, params);
    if (!decision.allowed) {
      throw new RpcDeniedError(method, decision.reason, decision.code);
    }
  }

  private setProblem(problem: string): void {
    this.state = { ...this.state, problem };
    this.publish();
  }
}

/** Wire-shaped helpers so the app layer never reaches into raw JSON. */
export const asChatList = (value: unknown): Chat[] => (Array.isArray(value) ? (value as Chat[]) : []);
export const asDeviceList = (value: unknown): Device[] =>
  Array.isArray(value) ? (value as Device[]) : [];
export const asSpaceList = (value: unknown): Space[] =>
  Array.isArray(value) ? (value as Space[]) : [];
export const asSessionList = (value: unknown): Session[] =>
  Array.isArray(value) ? (value as Session[]) : [];
